import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox, Table } from '@fikasio/react-ui-components';
import CompletionTimeEntryModal from 'components/tasks/CompletionTimeEntryModal';
import EmptyTableRow from 'components/UI/EmptyTableRow';
import { usePatchTaskMutation } from 'services/tasks/api';
import { Task } from 'services/tasks/types';
import { nestedChildIds, withoutCollapsedDescendants } from 'utils/nestedRows';
import { nestTasks } from '../utils';
import TaskRow from './TaskRow';
import { OpenStatusDropdownProvider } from './TaskStatusDropdown';
import styles from './TasksList.module.css';

const TasksList = ({
  onAddTask,
  onSelectAllTasks,
  onTaskClick,
  onTaskSelect,
  projectId,
  selectedTasks,
  taskDefaults,
  tasks,
}) => {
  const { t } = useTranslation();
  const [patchTask] = usePatchTaskMutation();
  const [collapsedIds, setCollapsedIds] = useState<Set<string>>(() => new Set());
  const [completionTask, setCompletionTask] = useState<Task | null>(null);
  const [taskIdToFocus, setTaskIdToFocus] = useState<string | null>(null);

  const toggleCollapsed = (taskId: string) => {
    setCollapsedIds(current => {
      const next = new Set(current);
      if (next.has(taskId)) {
        next.delete(taskId);
      } else {
        next.add(taskId);
      }
      return next;
    });
  };

  const reveal = (taskId?: string | null) => {
    if (!taskId) {
      return;
    }
    setCollapsedIds(current => {
      if (!current.has(taskId)) {
        return current;
      }
      const next = new Set(current);
      next.delete(taskId);
      return next;
    });
  };

  const allTasksAreChecked = !!tasks?.length
    && (tasks?.length === selectedTasks.length);

  const nestedTasks = nestTasks(tasks || []);
  const collapsibleIds = nestedChildIds(nestedTasks, row => row.task.id);
  const visibleTasks = withoutCollapsedDescendants(
    nestedTasks,
    row => !!row.task.id && collapsedIds.has(row.task.id),
  );

  const addTask = async task => {
    reveal(task.parentTaskId);
    onAddTask(task)
      .then(result => {
        const resultTask = result?.data;
        if (resultTask?.id) {
          setTaskIdToFocus(resultTask.id);
        }
      });
  };

  return (
    <div className={styles.tasksList}>
      {
        completionTask?.id && (
          <CompletionTimeEntryModal
            onHide={() => setCompletionTask(null)}
            onSave={patch => {
              patchTask({
                id: completionTask.id,
                ...patch,
              });
            }}
            task={completionTask}
          />
        )
      }
      <OpenStatusDropdownProvider>
        <Table
          bordered
          hover
          responsive
        >
        <thead>
          <tr>
            <th
              style={{ width: 35 }}
            >
              <Checkbox
                isChecked={allTasksAreChecked}
                onClick={() => {
                  if (allTasksAreChecked) {
                    onSelectAllTasks([]);
                  } else {
                    onSelectAllTasks(tasks);
                  }
                }}
              />
            </th>
            <th style={{ width: 35 }} />
            <th />
            <th style={{ width: 160 }} />
            <th style={{ width: 80 }} />
            <th style={{ width: 110 }} />
            <th style={{ width: 35 }} />
            <th style={{ width: 35 }} />
            <th style={{ width: 35 }} />
          </tr>
        </thead>
        <tbody>
          {
            (!tasks || !tasks.length) && (
              <EmptyTableRow
                action={{
                  label: t('createATask'),
                  onClick: () => addTask({
                    description: '',
                    type: 'Template',
                    ...taskDefaults,
                  }),
                }}
                colSpan={9}
                message={t('noTasksToDisplay')}
              />
            )
          }
          {
            visibleTasks.map(({ depth, parent, previousSibling, task }) => (
              <TaskRow
                autoFocus={task.id === taskIdToFocus}
                canCollapse={!!task.id && collapsibleIds.has(task.id)}
                depth={depth}
                isCollapsed={!!task.id && collapsedIds.has(task.id)}
                isSelected={!!selectedTasks.find(t1 => task.id === t1.id)}
                key={task.id}
                onAddTask={addTask}
                onClick={onTaskClick}
                onSelect={onTaskSelect}
                onTaskCompleted={setCompletionTask}
                onToggleCollapsed={() => task.id && toggleCollapsed(task.id)}
                parent={parent}
                previousSibling={previousSibling}
                projectId={projectId}
                task={task}
                taskDefaults={taskDefaults}
              />
            ))
          }
        </tbody>
        </Table>
      </OpenStatusDropdownProvider>
    </div>
  );
};

export default TasksList;
